// 紫の技を、ほかの色へ替える比較版 (2026-10-11・開発版だけの ?purpleSwap=a|b|c)。
//
// 🔴 退役 (10-11 朝の答え purple-swap-pick の一言「赤の技、紫の技っていってるのはボスの体の周りをオーラで包むやつだよ。
//   全部の技のテクスチャなどに対しても色を変えようとしているわけじゃない」)。技の絵の色は替えない＝この比較版は読み違い。
//   開発版のスイッチだけ残す (配布版は元から通らない)。台帳 docs/ledger/story.md 2026-10-11。
//
// 出所: 10-11 ユーザー「紫はボスの致命とかパリィじゃないと対処できない攻撃として使って」。
//   答えるページ purple-attack-scope の答え＝「① 言葉どおり。紫はボスの致命と、これから作るパリィでしか返せない技だけ。
//   今ある紫の技9件は全部ほかの色へ」。替える色は未定 → この比較版で見比べて決める。
//   9件の一覧＝docs/research/2026-10-11-purple-usage-audit.md §5-2。
//
// 9件の分け方:
//   弾けない (位置で避ける) ＝ 'unparry'
//     亡霊の波の霧と柱 / 戴冠獣ののしかかりの吠え / 花嫁ヴェールの招待 / 花嫁の破れたバージンロード (薔薇の茨) /
//     鶏・先代の床の技の予告
//   弾ける ＝ 'parry'
//     花嫁の裾乱れ (回る連撃) / 介錯の据えの一打のオーラ / 介錯の砲 (変形の光・溜め・弾・発勁) / 覚醒王の大暴れの紫の怨念
//     ⚠️ 据えの一打 (exec_maulSet) のオーラは 08 月に外してあり、今は出ない (attacks.ts)。render.ts の auraStrength の 'swap' は、
//        auraTint 'charge' の技 (今は使っていない旧の特大縦振りだけ) が出た時に 'parry' の色で描くための物。
//   一覧の外で見つけた物: 介錯の山なり榴弾 (exec_cannonMortar) は弾けないのに紫の玉で飛ぶ → 'unparry' へ。
//     花嫁の裾乱れの最後の叩きつけ (render.ts drawDownswingArc の 'veil') も裾乱れの一部として 'parry' へ。
//
// 色の替え方: 今の紫の色を1つずつ書き換えるのではなく、「明るさは今のまま、色味だけ替える」写し方を1つ持つ。
//   今の FX は暗い紫 → 明るい藤 → 白っぽいすみれ、と明るさで役を分けて描いてあるので、
//   明るさ (人の目の明るさ＝相対輝度) をそろえたまま色相と彩度だけを、案ごとの「暗い所・中ほど・明るい所」の3つの色へ写す。
//   強さの比べ方が公平になる (明るさが同じなので、色の違いだけを見比べられる)。
//   紫〜青〜赤紫の色相 (190〜335度) で、彩度が少しでもある色だけを写す。灰・黒・白・赤・金・黄緑はそのまま。
//   絵 (PNG) は元を上書きせず、読み込んだ後に同じ写し方で塗り替えた写しを1回だけ作って描く (swapImage)。
//
// 🔴 配布版は不変: 案を読むのは src/game.ts の開発版の箱の中だけ。案が無い時 (null) は、どの関数も入れた色をそのまま返す。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs が配布ビルドの文字列を見張る)。

export type PurpleSwapVariant = 'a' | 'b' | 'c'
export type PurpleSwapRole = 'unparry' | 'parry'

/** 色相 h (度)・彩度 s (0..1)。明るさは元の色から引き継ぐ。 */
export type SwapTone = { h: number; s: number }
/** yMul = 明るさの掛け目。色の無い灰・銀は、同じ輝度でも色のある光より暗く見える (ヘルムホルツ＝コールラウシュ効果) ので、
 *  灰・銀の案だけ 1.4 倍にして「同じ強さに見える」所へ寄せる。色のある案は 1 (輝度そのまま)。 */
export type SwapRamp = { label: string; dark: SwapTone; mid: SwapTone; light: SwapTone; yMul: number }

/**
 * 案ごとの色。暗い所 (元の明度 0.15 以下)・中ほど (0.5)・明るい所 (0.85 以上) の3つ。間はなめらかにつなぐ。
 *   a = 赤と銀 (推奨) / b = 赤と橙 / c = 灰白と青緑。
 *   赤の3つは、全ボス共通の「弾けない」オーラ (render.ts CURSE_DEEP #7a0b14・CURSE_MID #b01822・CURSE_BRIGHT #e13a3a) に寄せた。
 *   明るい所の赤は、ピンクに見えないよう朱 (橙寄り) へ倒す (明るい赤の光は、実物でも橙白に見える)。
 */
export const PURPLE_SWAP_RAMPS: Readonly<Record<PurpleSwapVariant, Readonly<Record<PurpleSwapRole, SwapRamp>>>> = {
  a: {
    unparry: { label: '赤', dark: { h: 352, s: 0.8 }, mid: { h: 2, s: 0.85 }, light: { h: 18, s: 1 }, yMul: 1 },
    parry: { label: '銀', dark: { h: 222, s: 0.12 }, mid: { h: 220, s: 0.08 }, light: { h: 210, s: 0.06 }, yMul: 1.4 },
  },
  b: {
    unparry: { label: '赤', dark: { h: 352, s: 0.8 }, mid: { h: 2, s: 0.85 }, light: { h: 18, s: 1 }, yMul: 1 },
    parry: { label: '橙', dark: { h: 16, s: 0.78 }, mid: { h: 26, s: 0.9 }, light: { h: 34, s: 0.95 }, yMul: 1 },
  },
  c: {
    unparry: { label: '灰白', dark: { h: 30, s: 0.06 }, mid: { h: 36, s: 0.07 }, light: { h: 42, s: 0.12 }, yMul: 1.4 },
    parry: { label: '青緑', dark: { h: 182, s: 0.62 }, mid: { h: 174, s: 0.6 }, light: { h: 166, s: 0.55 }, yMul: 1 },
  },
}

let current: PurpleSwapVariant | null = null

/** URL の値を読む。a / b / c 以外は null (今のまま)。 */
export function parsePurpleSwap(v: unknown): PurpleSwapVariant | null {
  const k = typeof v === 'string' ? v.toLowerCase() : ''
  return k === 'a' || k === 'b' || k === 'c' ? k : null
}
/** 開発版の箱 (src/game.ts) とテストだけが呼ぶ。null = 今のまま。 */
export function setPurpleSwap(v: PurpleSwapVariant | null): void {
  if (v !== current) { strCache.clear() }
  current = v
}
export function getPurpleSwap(): PurpleSwapVariant | null { return current }

// ---------------------------------------------------------------- 色の写し方 (純関数)

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
const toLin = (c: number): number => {
  const x = c / 255
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
}
/** 相対輝度 (人の目の明るさ)。 */
export function relLuminance(r: number, g: number, b: number): number {
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b)
}
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const mx = Math.max(rn, gn, bn), mn = Math.min(rn, gn, bn)
  const l = (mx + mn) / 2
  if (mx === mn) return [0, 0, l]
  const d = mx - mn
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
  let h: number
  if (mx === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
  else if (mx === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return [h * 60, s, l]
}
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hh = (((h % 360) + 360) % 360) / 360
  if (s <= 0) return [l * 255, l * 255, l * 255]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const f = (t: number): number => {
    let x = t
    if (x < 0) x += 1
    if (x > 1) x -= 1
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  return [f(hh + 1 / 3) * 255, f(hh) * 255, f(hh - 1 / 3) * 255]
}
const lerpHue = (a: number, b: number, t: number): number => {
  let d = b - a
  if (d > 180) d -= 360
  if (d < -180) d += 360
  return a + d * t
}
function toneAt(ramp: SwapRamp, l: number): SwapTone {
  const t = clamp01((l - 0.15) / 0.7)
  const [p, q, u] = t < 0.5 ? [ramp.dark, ramp.mid, t * 2] : [ramp.mid, ramp.light, (t - 0.5) * 2]
  return { h: lerpHue(p.h, q.h, u), s: p.s + (q.s - p.s) * u }
}

/** 写す対象の色か (紫〜青〜赤紫の色相で、彩度が少しでもある)。 */
export function isSwapTarget(r: number, g: number, b: number): boolean {
  const [h, s, l] = rgbToHsl(r, g, b)
  if (s < 0.05 || l < 0.015 || l > 0.985) return false
  return h >= 190 && h <= 335
}

/**
 * 1色を写す (案を引数で受ける純関数・テスト用)。対象外の色はそのまま返す。
 *   明るさ (相対輝度) は元の色 × 案の yMul にそろえる。
 */
export function swapRgbWith(variant: PurpleSwapVariant, role: PurpleSwapRole, r: number, g: number, b: number): [number, number, number] {
  return swapRgbRamp(PURPLE_SWAP_RAMPS[variant][role], r, g, b)
}

/** 1色を指定の色の組へ写す (介錯の常夜燈の青 src/exec_tint.ts もこれを使う)。 */
export function swapRgbRamp(ramp: SwapRamp, r: number, g: number, b: number): [number, number, number] {
  if (!isSwapTarget(r, g, b)) return [r, g, b]
  const [, s0, l0] = rgbToHsl(r, g, b)
  const tone = toneAt(ramp, l0)
  const satK = clamp01((s0 - 0.05) / 0.3)
  const s = tone.s * (satK * satK * (3 - 2 * satK))
  const y = Math.min(1, relLuminance(r, g, b) * ramp.yMul)
  let lo = 0, hi = 1
  for (let i = 0; i < 22; i++) {
    const mid = (lo + hi) / 2
    const [rr, gg, bb] = hslToRgb(tone.h, s, mid)
    if (relLuminance(rr, gg, bb) < y) lo = mid
    else hi = mid
  }
  const [rr, gg, bb] = hslToRgb(tone.h, s, (lo + hi) / 2)
  return [Math.round(rr), Math.round(gg), Math.round(bb)]
}

/** 今の案で1色を写す。案が無い時はそのまま。 */
export function swapRgb(role: PurpleSwapRole, r: number, g: number, b: number): [number, number, number] {
  return current ? swapRgbWith(current, role, r, g, b) : [r, g, b]
}

const strCache = new Map<string, string>()
const hex2 = (n: number): string => n.toString(16).padStart(2, '0')

/**
 * 色の文字列を写す。受け付ける形: 'r,g,b' (rgba() の中身) / '#rrggbb' / 'rgb(r,g,b)' / 'rgba(r,g,b,a)'。
 *   形はそのまま (hex なら hex・'r,g,b' なら 'r,g,b') で返す。案が無い時は入れた文字列をそのまま返す (配布版と同じ)。
 */
export function sw(role: PurpleSwapRole, css: string): string {
  if (!current) return css
  const key = `${role}|${css}`
  const hit = strCache.get(key)
  if (hit !== undefined) return hit
  const out = swapCssWith(current, role, css)
  strCache.set(key, out)
  return out
}

/** sw の純関数版 (案を引数で受ける・テスト用)。 */
export function swapCssWith(variant: PurpleSwapVariant, role: PurpleSwapRole, css: string): string {
  return swapCssRamp(PURPLE_SWAP_RAMPS[variant][role], css)
}

/** 色の文字列を指定の色の組へ写す (形はそのまま)。 */
export function swapCssRamp(ramp: SwapRamp, css: string): string {
  const s = css.trim()
  let m = /^#([0-9a-fA-F]{6})$/.exec(s)
  if (m) {
    const n = parseInt(m[1], 16)
    const [r, g, b] = swapRgbRamp(ramp, (n >> 16) & 255, (n >> 8) & 255, n & 255)
    return `#${hex2(r)}${hex2(g)}${hex2(b)}`
  }
  m = /^(\d+)\s*,\s*(\d+)\s*,\s*(\d+)$/.exec(s)
  if (m) {
    const [r, g, b] = swapRgbRamp(ramp, +m[1], +m[2], +m[3])
    return `${r},${g},${b}`
  }
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(,\s*[\d.]+\s*)?\)$/.exec(s)
  if (m) {
    const [r, g, b] = swapRgbRamp(ramp, +m[1], +m[2], +m[3])
    return m[4] ? `rgba(${r},${g},${b}${m[4].replace(/\s+/g, '')})` : `rgb(${r},${g},${b})`
  }
  return css
}

/** [r,g,b] の組を写す (案が無い時は同じ物を返す)。 */
export function swTriplet<T extends readonly [number, number, number]>(role: PurpleSwapRole, c: T): T | [number, number, number] {
  return current ? swapRgbWith(current, role, c[0], c[1], c[2]) : c
}

// ---------------------------------------------------------------- 絵 (PNG) の写し

type Swappable = CanvasImageSource & { naturalWidth?: number; naturalHeight?: number; width?: number | SVGAnimatedLength; height?: number | SVGAnimatedLength }
const imgCache = new WeakMap<object, Map<string, HTMLCanvasElement>>()

/**
 * 絵を写した写しを返す (読み込んだ後に1回だけ作る・元の絵は触らない)。案が無い・作れない時は元の絵をそのまま返す。
 *   描く手前で `ctx.drawImage(swapImage('parry', img), ...)` のように挟む。
 */
export function swapImage<T extends Swappable>(role: PurpleSwapRole, img: T): T | HTMLCanvasElement {
  if (!current) return img
  return swapImageRamp(`${current}|${role}`, PURPLE_SWAP_RAMPS[current][role], img)
}

/** 絵を指定の色の組へ写した写しを返す (key ごとに1回だけ作る)。作れない時は元の絵。 */
export function swapImageRamp<T extends Swappable>(key: string, ramp: SwapRamp, img: T): T | HTMLCanvasElement {
  if (typeof document === 'undefined') return img
  const w = Number((img as { naturalWidth?: number }).naturalWidth ?? (img as { width?: number }).width ?? 0)
  const h = Number((img as { naturalHeight?: number }).naturalHeight ?? (img as { height?: number }).height ?? 0)
  if (!(w > 0 && h > 0)) return img
  let byKey = imgCache.get(img as object)
  if (!byKey) { byKey = new Map(); imgCache.set(img as object, byKey) }
  const done = byKey.get(key)
  if (done) return done
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const g = c.getContext('2d', { willReadFrequently: true })
  if (!g) return img
  g.drawImage(img, 0, 0)
  let data: ImageData
  try { data = g.getImageData(0, 0, w, h) } catch { return img }
  const px = data.data
  const lut = new Map<number, number>()
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] === 0) continue
    const key24 = (px[i] << 16) | (px[i + 1] << 8) | px[i + 2]
    let out = lut.get(key24)
    if (out === undefined) {
      const [r, gg, b] = swapRgbRamp(ramp, px[i], px[i + 1], px[i + 2])
      out = (r << 16) | (gg << 8) | b
      lut.set(key24, out)
    }
    px[i] = (out >> 16) & 255; px[i + 1] = (out >> 8) & 255; px[i + 2] = out & 255
  }
  g.putImageData(data, 0, 0)
  byKey.set(key, c)
  return c
}
